import { useEffect, useRef, useState } from 'react'
import { Outlet, useOutletContext } from 'react-router'

import { driveSettingDescriptors } from 'config/driveSettings'
import useCurrentAccount, { useUpdateCurrentAccount } from 'query/useCurrentAccount'
import {
  Alert,
  Button,
  CheckboxField,
  Field,
  Form,
  FormActions,
  FormCard,
  Input,
  PageContent,
  PageHeader,
  TabPane,
  Tabs,
} from 'ui'

const ACCOUNT_SAVE_ERROR_MESSAGE = 'Failed to save account settings. Please try again.'

const parseDefaultSettings = value => {
  if (!value) return {}
  try {
    return JSON.parse(value)
  } catch (_error) {
    return {}
  }
}

export const SettingsPageComponent = () => {
  const { data: currentAccount = {}, setData: setCurrentAccount } = useCurrentAccount()
  const hasLoadedAccount = useRef(false)
  const [accountName, setAccountName] = useState('')
  const [savedAccountName, setSavedAccountName] = useState('')
  const [accountNameError, setAccountNameError] = useState('')
  const [settings, setSettings] = useState({})
  const [settingsError, setSettingsError] = useState('')
  const updateAccountMutation = useUpdateCurrentAccount()

  const setCurrentAccountData = updates => setCurrentAccount({ ...currentAccount, ...updates })

  useEffect(() => {
    if (hasLoadedAccount.current || !currentAccount.id) return
    hasLoadedAccount.current = true
    setAccountName(currentAccount.name || '')
    setSavedAccountName(currentAccount.name || '')
    setSettings(parseDefaultSettings(currentAccount.defaultSettings))
  }, [currentAccount])

  const isAccountNameDirty = accountName !== savedAccountName
  const isSavingAccountName = updateAccountMutation.isPending
  const accountNameSaveDisabled = !isAccountNameDirty || isSavingAccountName

  const handleSaveAccountName = event => {
    event.preventDefault()
    if (accountNameSaveDisabled) return

    const nextAccountName = accountName
    setAccountNameError('')
    updateAccountMutation.mutate(
      { accountName: nextAccountName },
      {
        onError: error => setAccountNameError(error?.message || ACCOUNT_SAVE_ERROR_MESSAGE),
        onSuccess: response => {
          const savedName = response.name ?? nextAccountName
          setCurrentAccountData({ ...response, name: savedName })
          setAccountName(savedName)
          setSavedAccountName(savedName)
        },
      },
    )
  }

  const handleChangeSettings = (event, field) => {
    const previousSettings = settings
    const newSettings = { ...settings, [field]: event.target.checked }
    setSettings(newSettings)
    setSettingsError('')
    updateAccountMutation.mutate(
      { settings: newSettings },
      {
        onError: error => {
          setSettings(previousSettings)
          setSettingsError(error?.message || ACCOUNT_SAVE_ERROR_MESSAGE)
        },
        onSuccess: response => setCurrentAccountData(response),
      },
    )
  }

  return (
    <>
      <PageHeader
        subtitle="Manage your team name and the drive defaults applied to new devices."
        title="Account Settings"
      />
      <PageContent>
        <Tabs>
          <TabPane key="account" tab="Account" to="/settings" />
          <TabPane key="defaults" tab="Drive Defaults" to="/settings/defaults" />
        </Tabs>
        <div className="tw:mx-auto tw:max-w-form">
          <Outlet
            context={{
              accountName,
              accountNameError,
              accountNameSaveDisabled,
              handleChangeSettings,
              handleSaveAccountName,
              isSavingAccountName,
              setAccountName,
              settings,
              settingsError,
            }}
          />
        </div>
      </PageContent>
    </>
  )
}

const useSettingsPageContext = () => useOutletContext()

export const SettingsAccountTab = () => {
  const {
    accountName,
    accountNameError,
    accountNameSaveDisabled,
    handleSaveAccountName,
    isSavingAccountName,
    setAccountName,
  } = useSettingsPageContext()

  return (
    <Form onSubmit={handleSaveAccountName}>
      <FormCard title="Account Settings">
        {accountNameError && <Alert color="danger">{accountNameError}</Alert>}
        <Field label="Team Name">
          <Input id="teamName" name="teamName" onChange={event => setAccountName(event.target.value)} value={accountName} />
        </Field>
      </FormCard>
      <FormActions className="tw:mt-5">
        <Button disabled={accountNameSaveDisabled} type="submit">
          {isSavingAccountName ? 'Saving…' : 'Save'}
        </Button>
      </FormActions>
    </Form>
  )
}

export const SettingsDefaultsTab = () => {
  const { handleChangeSettings, settings, settingsError } = useSettingsPageContext()

  return (
    <FormCard description="Choose the settings applied when a new device joins your team." title="Default Drive Settings">
      {settingsError && <Alert className="tw:mb-4" color="danger">{settingsError}</Alert>}
      <Form onSubmit={event => event.preventDefault()}>
        {driveSettingDescriptors.map(({ field, helper, label }) => (
          <CheckboxField
            checked={Boolean(settings[field])}
            helper={helper}
            key={field}
            label={label}
            onChange={event => handleChangeSettings(event, field)}
          />
        ))}
      </Form>
    </FormCard>
  )
}

export const SettingsPage = SettingsPageComponent
